import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import extend from 'lodash/extend';
import UserDatabases from '@Services/UserDatabases';
import store from '@Store';

import forEach from 'lodash/forEach';

import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';

import { initialState } from './FindSimilarModalReducer';
import {
  typeOfFindSimilarModal,
  findSimilarModalSetDataReducerType,
  findSimilarModalSetIsLoadingReducerType,
  findSimilarModalSetCurrentDbReducerType,
  findSimilarModalSetAvailableDatabasesReducerType,
  findSimilarModalSetDatabasesOrderReducerType,
} from './FindSimilarModalConstants';

import {
  data as dataSelector,
  currentDb as currentDbSelector,
  databasesOrder as databasesOrderSelector,
} from './FindSimilarModalSelectors';

const reaxysDbId = '8';

export const openFindSimilarModal = (args) => {
  return (dispatch) => {
    dispatch(
      updateDialog({
        id: typeOfFindSimilarModal,
        dialogType: typeOfFindSimilarModal,
        movable: false,
        backdrop: true,
        ...args,
      })
    );
  };
};

export const closeFindSimilarModal = () => {
  return (dispatch) => {
    dispatch(
      closeDialog({
        id: typeOfFindSimilarModal,
      })
    );
  };
};

export const dispatchOpenFindSimilarModal = (...args) => {
  store.dispatch(openFindSimilarModal(...args));
};

export function setIsLoading(isLoading) {
  return {
    type: findSimilarModalSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setData(data) {
  return {
    type: findSimilarModalSetDataReducerType,
    payload: { data },
  };
}

export function setCurrentDb(currentDb) {
  return {
    type: findSimilarModalSetCurrentDbReducerType,
    payload: { currentDb },
  };
}

export function setDatabasesOrder(databasesOrder) {
  return {
    type: findSimilarModalSetDatabasesOrderReducerType,
    payload: { databasesOrder },
  };
}

export function setAvailableDatabases(availableDatabases) {
  return {
    type: findSimilarModalSetAvailableDatabasesReducerType,
    payload: { availableDatabases },
  };
}

export const goTo = (callback, result) => {
  return (dispatch, getState) => {
    const data = dataSelector(getState());

    if (callback) {
      callback({
        selectedResult: result,
        allResults: data.items,
      });
    }
    dispatch(closeFindSimilarModal());
  };
};

export const close = (callback) => {
  return (dispatch) => {
    if (callback) {
      callback();
    }
    dispatch(closeFindSimilarModal());
  };
};

export const resetState = () => {
  return (dispatch) => {
    dispatch(setIsLoading(initialState.get('isLoading')));
    dispatch(setData(initialState.get('data')));
    dispatch(setCurrentDb(initialState.get('currentDb')));
    dispatch(setDatabasesOrder(initialState.get('databasesOrder')));
    dispatch(setAvailableDatabases(initialState.get('availableDatabases')));
  };
};

export const _isSomeCountsForCurrentDbReceived = () => {
  return (dispatch, getState) => {
    const data = dataSelector(getState());
    const currentDb = currentDbSelector(getState());
    const items = get(data, 'items');
    return items && items.filter((item) => item.databaseId === currentDb.databaseId).some((item) => !isUndefined(item.resultSize));
  };
};

export const _handleDatabaseResults = () => {
  return (dispatch, getState) => {
    const data = dataSelector(getState());
    const currentDb = currentDbSelector(getState());
    const items = get(data, 'items');
    if (!items) {
      return;
    }

    const availableDatabases = uniq(items.map((item) => item.databaseId));
    if (availableDatabases.indexOf(currentDb.databaseId) === -1) {
      const databaseId = availableDatabases[0];
      dispatch(setCurrentDb({ dbName: UserDatabases.getDbName(databaseId), databaseId }));
    }
  };
};

export const _areAllCountsReceived = () => {
  return (dispatch, getState) => {
    const data = dataSelector(getState());
    const items = get(data, 'items');
    return items && items.every((item) => item.resultSize >= 0);
  };
};

export const _sortDatabaseIds = (ids, databasesOrder) => {
  const orderedIds = [];

  ids.forEach((id) => {
    orderedIds[databasesOrder.indexOf(id)] = id;
  });

  return orderedIds;
};

export const _handleIntegratedDatabaseResults = () => {
  return (dispatch, getState) => {
    const data = dataSelector(getState());
    const databasesOrder = databasesOrderSelector(getState());
    const items = get(data, 'items');
    if (!items) {
      return;
    }
    // This is done to make Reaxys DB Id the first item in the list
    let dbs = _sortDatabaseIds(uniq(items.map((item) => item.databaseId)), databasesOrder);
    const index = dbs.indexOf(reaxysDbId);
    if (index !== -1) {
      dbs.splice(index, 1);
      dbs.unshift(reaxysDbId);
    }

    dbs = compact(dbs);
    dispatch(setAvailableDatabases(dbs));
  };
};

export const createUpdateResultHandler = (handleDatabaseResults, checkResults) => (data) => {
  return (dispatch, getState) => {
    if (data.isHandled) {
      return;
    }
    const dataState = dataSelector(getState());

    const items = get(data, 'payload.items');
    if (items) {
      if (!dataState) {
        dispatch(setData(data.payload));
      }

      dispatch(handleDatabaseResults());
    } else if (dataState) {
      const updatedItems = dataState.items.map((item) => {
        if (data.payload.queryId === item.queryId) {
          extend(item, data.payload);
        }
        return item;
      });

      dispatch(
        setData({
          ...dataState,
          items: updatedItems,
        })
      );
    }

    if (dispatch(checkResults())) {
      dispatch(setIsLoading(false));
    }

    // eslint-disable-next-line no-param-reassign
    data.isHandled = true;
  };
};

export const updateResults = (results, handler) => {
  return (dispatch) => {
    forEach(results, (result) => {
      dispatch(handler(result));
    });
  };
};

export const initialize = (modalProps, observer) => {
  return (dispatch) => {
    dispatch(resetState());
    dispatch(setIsLoading(true));

    const { currentDb, databasesOrder, isIntegratedModal } = modalProps;
    dispatch(setCurrentDb(currentDb));

    if (isIntegratedModal) {
      dispatch(setDatabasesOrder(databasesOrder));
      const updateResultHandler = createUpdateResultHandler(_handleIntegratedDatabaseResults, _areAllCountsReceived);
      observer.addObserver((results) => dispatch(updateResults(results, updateResultHandler)));
      return;
    }

    const updateResultHandler = createUpdateResultHandler(_handleDatabaseResults, _isSomeCountsForCurrentDbReceived);
    observer.addObserver((results) => dispatch(updateResults(results, updateResultHandler)));
  };
};
